<template>
  <div
    data-testid="sf-markdown-document-link-picker"
    class="
      tw-z-mentions
      tw-rounded-lg
      tw-border
      tw-border-solid
      tw-border-sf-neutral-lighter-1
      tw-bg-white
      tw-shadow-md
    "
    @keydown="handleDOMKeyDown"
  >
    <!-- Step 1: Category selection -->
    <div
      v-if="currentStep === Steps.TYPE_SELECTION"
      class="tw-rounded-lg tw-overflow-hidden"
    >
      <div
        class="
          tw-px-3
          tw-py-2
          tw-border-0
          tw-border-b
          tw-border-solid
          tw-border-sf-neutral-lighter-1
          tw-bg-sf-snow
        "
      >
        <div
          class="tw-text-xs tw-font-semibold tw-text-sf-neutral tw-uppercase"
        >
          {{
            I18n.t(
              'components.markdown_editor.document_link_picker.insert_link_to'
            )
          }}
        </div>
      </div>
      <SfList class="tw-m-0">
        <SfListItem
          v-for="(type, index) in documentTypes"
          :key="type.value"
          v-scroll-into-view="
            index === selectedIndex ? selectedScrollOptions : false
          "
          variant="lg"
          :item-id="type.value"
          :selected="index === selectedIndex"
          class="tw-py-[6px] tw-px-3"
          @item-click="selectDocumentType(type.value)"
        >
          {{ type.label }}
        </SfListItem>
      </SfList>
    </div>

    <!-- Step 2: Document list with search -->
    <div
      v-else-if="currentStep === Steps.DOCUMENT_LIST"
      class="tw-rounded-lg tw-overflow-hidden tw-w-[450px]"
    >
      <div
        class="
          tw-px-3
          tw-py-2
          tw-pb-1
          tw-border-0
          tw-border-solid
          tw-border-b-sf-neutral-lighter-1
          tw-border-b
          tw-bg-sf-snow
        "
      >
        <div class="tw-text-xs tw-text-sf-primary tw-mb-1 tw-uppercase">
          <span
            ref="breadcrumbRef"
            tabindex="0"
            class="
              tw-cursor-pointer
              hover:tw-underline
              focus:tw-underline
              tw-outline-none
            "
            @click="goBackToTypeSelection"
            @keydown.enter.stop.prevent="goBackToTypeSelection"
          >
            {{
              I18n.t(
                'components.markdown_editor.document_link_picker.insert_link_to'
              )
            }}
          </span>
          <span
            class="
              tw-text-sf-neutral-dark-1
              before:tw-content-['>'] before:tw-text-sf-neutral before:tw-mx-2
            "
          >
            {{ selectedDocumentTypeLabel }}
          </span>
        </div>
      </div>

      <div class="tw-p-3">
        <SfSearchInput
          :model-value="searchQuery"
          :placeholder="
            I18n.t(
              'components.markdown_editor.document_link_picker.type_to_search'
            )
          "
          :show-result-navigation="false"
          :show-clear-icon="!!searchQuery"
          :focused-at="searchFocusedAt"
          @update:model-value="updateSearchQuery"
          @clear="clearSearch"
        />

        <div
          v-if="isLoading"
          class="tw-px-4 tw-py-8 tw-text-center tw-text-sf-neutral-light-1"
        >
          <i class="fa fa-spinner fa-spin tw-mr-2"></i>
          {{
            I18n.t('components.markdown_editor.document_link_picker.loading')
          }}
        </div>

        <!-- For permanent and period-specific documents, use folder structure -->
        <div
          v-else-if="hasFolderStructure"
          class="tw-max-w-lg tw-min-w-[20rem] tw-mt-3"
        >
          <ExistingDocumentsTable
            :entries="entries"
            :selected-entries-ids="[]"
            :permanent-category="permanentCategory ?? undefined"
            :scroll-offset="0"
            :for-sidebar="true"
            :show-view-button="true"
            @navigate="navigateToCategory"
            @toggle-selection="handleDocumentSelection"
            @view-document="viewDocument"
          />
          <SfPagination
            v-if="
              selectedDocumentType === DocumentTypeValues.PERMANENT_DOCUMENT &&
              totalPages > 1
            "
            :current-page="page"
            :total-pages="totalPages"
            class="tw-mt-2"
            @change-page="changePage"
          />
        </div>

        <!-- For account entries, reconciliation templates and exports, use flat list -->
        <div v-else>
          <div
            v-if="shouldShowNoDocuments"
            class="tw-px-4 tw-py-8 tw-text-center tw-text-sf-neutral-light-1"
          >
            {{
              I18n.t(
                'components.markdown_editor.document_link_picker.no_items_found'
              )
            }}
          </div>

          <div
            v-else
            class="
              tw-border
              tw-rounded-lg
              tw-border-solid
              tw-border-sf-neutral-lighter-2
              tw-my-2
              tw-overflow-hidden
            "
          >
            <SfList class="tw-m-0 tw-max-h-60 tw-overflow-y-auto">
              <SfListItem
                v-for="(document, index) in filteredEntries"
                :key="document.id"
                v-scroll-into-view="
                  index === selectedIndex ? selectedScrollOptions : false
                "
                variant="lg"
                :item-id="String(document.id)"
                :selected="index === selectedIndex"
                class="tw-group tw-relative tw-py-[6px]"
                @item-click="handleDocumentSelection(document)"
              >
                <div class="sf-level sf-level-small tw-pr-5">
                  <span class="tw-px-1 !tw-m-0" />
                  {{ document.title }}
                  <button
                    type="button"
                    tabindex="-1"
                    class="
                      tw-absolute tw-right-2 tw-top-1/2
                      -tw-translate-y-1/2
                      tw-opacity-0
                      group-hover:tw-opacity-100
                      tw-transition-opacity
                      tw-duration-200
                      tw-bg-transparent
                      tw-text-sf-neutral-dark-1
                      hover:tw-text-sf-primary
                      tw-border-none tw-outline-none tw-cursor-pointer
                    "
                    :title="
                      I18n.t(
                        'components.markdown_editor.document_link_picker.view_document'
                      )
                    "
                    @click.stop="viewDocument(document)"
                  >
                    <i class="fa fa-external-link-alt tw-text-sm"></i>
                  </button>
                </div>
              </SfListItem>
            </SfList>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, shallowRef, computed, watch, nextTick } from 'vue'
import { useDebounceFn } from '@vueuse/core'
import fuzzysort from 'fuzzysort'

import SfList from '@app/shared/vue/components/sf-list/sf-list.vue'
import SfListItem from '@app/shared/vue/components/sf-list/sf-list-item.vue'
import SfSearchInput from '@app/shared/vue/components/sf-search/search-input.vue'
import ExistingDocumentsTable from '@app/application/reconciliation/sections/existing-documents-table.vue'
import SfPagination from '@app/shared/vue/components/sf-pagination/sf-pagination.vue'
import useI18n from '@app/shared/vue/composables/use-I18n'
import type { DocumentTypeValue, Step } from './constants'
import { DocumentTypeValues, Steps } from './constants'
import {
  getDocumentTypeLabels,
  isCached,
  buildBaseParams,
  getCompanyId,
} from './utils'
import type { ParamsValue } from './types'
import { SfLog } from '@app/adapters/logger'
import scrollIntoViewDirective from '@app/shared/vue/directives/scrollIntoView'
import { useMentionablesStore } from '@app/application/communication/store/mentionables'
import {
  fetchPermanentDocuments,
  fetchPeriodSpecificDocuments,
} from '@app/application/communication/api'
import type {
  PermanentCategory,
  DocumentEntry,
} from '@app/application/communication/types'

interface Props {
  command: (payload: {
    id: string
    link: string
    mentionType: DocumentTypeValue
  }) => void
}

const props = defineProps<Props>()

// Required by TipTap's suggestion plugin: the VueRenderer calls `ref.onKeyDown()`
// on the component instance to forward keyboard events from the editor.
defineExpose({
  onKeyDown,
})

const vScrollIntoView = scrollIntoViewDirective
// Each navigation assigns a new object so the v-scroll-into-view directive always
// detects a reference change and triggers scrollIntoView — even when selectedIndex
// doesn't change (e.g. wrap-around back to the same position after manual scrolling).
const selectedScrollOptions = shallowRef<{ block: 'nearest' } | false>(false)

const I18n = useI18n()
const mentionablesStore = useMentionablesStore()

const currentStep = ref<Step>(Steps.TYPE_SELECTION)
const selectedDocumentType = ref<DocumentTypeValue | null>(null)
const searchQuery = ref('')
const searchFocusedAt = ref(0)
const fetchedEntries = ref<DocumentEntry[]>([])
const permanentCategory = ref<PermanentCategory | null>(null)
const page = ref(1)
const totalPages = ref(1)
const loading = ref(false)
const selectedIndex = ref(0)
const selectionMade = ref(false)

const breadcrumbRef = ref<HTMLElement | null>(null)

const isLoading = computed(() => {
  if (loading.value) return true

  if (!selectedDocumentType.value) return false

  const storeLoadingMap: Record<DocumentTypeValue, boolean> = {
    [DocumentTypeValues.ACCOUNT_ENTRY]: mentionablesStore.isLoadingAccounts,
    [DocumentTypeValues.RECONCILIATION_TEMPLATE]:
      mentionablesStore.isLoadingReconTemplates,
    [DocumentTypeValues.EXPORT]: mentionablesStore.isLoadingExports,
    [DocumentTypeValues.PERMANENT_DOCUMENT]: false,
    [DocumentTypeValues.PERIOD_SPECIFIC_DOCUMENT]: false,
  }

  return storeLoadingMap[selectedDocumentType.value]
})

const entries = computed(() => {
  if (!selectedDocumentType.value) return []

  const storeEntriesMap: Record<DocumentTypeValue, DocumentEntry[]> = {
    [DocumentTypeValues.ACCOUNT_ENTRY]: mentionablesStore.mentionAccounts.map(
      (account) => ({
        id: account.id,
        title: account.id,
        link: account.link,
      })
    ),
    [DocumentTypeValues.RECONCILIATION_TEMPLATE]:
      mentionablesStore.mentionReconTemplates.map((recon) => ({
        id: recon.id,
        title: recon.id,
        link: recon.link,
      })),
    [DocumentTypeValues.EXPORT]: mentionablesStore.mentionExports.map(
      (exp) => ({
        id: exp.id,
        title: exp.id,
        link: exp.link,
      })
    ),
    [DocumentTypeValues.PERMANENT_DOCUMENT]: fetchedEntries.value,
    [DocumentTypeValues.PERIOD_SPECIFIC_DOCUMENT]: fetchedEntries.value,
  }

  return storeEntriesMap[selectedDocumentType.value]
})

const documentTypeLabels = getDocumentTypeLabels()

const documentTypes = Object.entries(documentTypeLabels).map(
  ([value, label]) => ({
    value: value as DocumentTypeValue,
    label,
  })
)

const selectedDocumentTypeLabel = computed(() => {
  const label = selectedDocumentType.value
    ? documentTypeLabels[selectedDocumentType.value]
    : ''
  return label ? label.toUpperCase() : ''
})

const hasFolderStructure = computed(
  () =>
    selectedDocumentType.value === DocumentTypeValues.PERMANENT_DOCUMENT ||
    selectedDocumentType.value === DocumentTypeValues.PERIOD_SPECIFIC_DOCUMENT
)

const filteredEntries = computed(() => {
  if (hasFolderStructure.value || !searchQuery.value) {
    return entries.value
  }

  const results = fuzzysort.go(searchQuery.value, entries.value, {
    keys: ['title'],
    threshold: -1000,
  })

  return results.map((result) => result.obj)
})

const shouldShowNoDocuments = computed(() => {
  if (isLoading.value) return false
  return filteredEntries.value.length === 0
})

const currentListItems = computed(() => {
  if (currentStep.value === Steps.TYPE_SELECTION) {
    return documentTypes
  }
  return filteredEntries.value
})

watch(currentListItems, () => {
  selectedIndex.value = 0
  selectedScrollOptions.value = { block: 'nearest' }
})

const debouncedFetchDocuments = useDebounceFn(
  (params: Record<string, ParamsValue>) => {
    fetchDocuments(params)
  },
  300
)

watch(searchQuery, (newQuery) => {
  // Early exit for account entries, reconciliation templates and exports - they use client-side filtering
  if (!hasFolderStructure.value) return

  const params: Record<string, ParamsValue> = newQuery ? { q: newQuery } : {}
  debouncedFetchDocuments(params)
})

function upHandler(): void {
  const length = currentListItems.value.length
  if (length > 0) {
    selectedIndex.value = (selectedIndex.value + length - 1) % length
    selectedScrollOptions.value = { block: 'nearest' }
  }
}

function downHandler(): void {
  const length = currentListItems.value.length
  if (length > 0) {
    selectedIndex.value = (selectedIndex.value + 1) % length
    selectedScrollOptions.value = { block: 'nearest' }
  }
}

function enterHandler(): void {
  if (currentStep.value === Steps.TYPE_SELECTION) {
    const type = documentTypes[selectedIndex.value]
    if (type) {
      selectDocumentType(type.value)
    }
  } else if (!hasFolderStructure.value) {
    const entry = filteredEntries.value[selectedIndex.value]
    if (entry) {
      handleDocumentSelection(entry)
    }
  }
}

function handleTabKey(event: KeyboardEvent): boolean {
  if (currentStep.value !== Steps.DOCUMENT_LIST) return false

  if (event.shiftKey) {
    // Shift+Tab: focus the breadcrumb
    breadcrumbRef.value?.focus()
    return true
  }

  // Tab: skip list, focus search input
  focusSearchInput()
  return true
}

function focusSearchInput(): void {
  searchFocusedAt.value = Date.now()
}

function handleKeyDown(event: KeyboardEvent): boolean {
  if (
    currentStep.value === Steps.DOCUMENT_LIST &&
    hasFolderStructure.value &&
    ['ArrowUp', 'ArrowDown', 'Enter'].includes(event.key)
  ) {
    return false
  }

  switch (event.key) {
    case 'ArrowUp':
      upHandler()
      return true
    case 'ArrowDown':
      downHandler()
      return true
    case 'Enter':
      enterHandler()
      return true
    case 'Tab':
      return handleTabKey(event)
    default:
      return false
  }
}

// For TipTap suggestion plugin
function onKeyDown({ event }: { event: KeyboardEvent }): boolean {
  return handleKeyDown(event)
}

// For direct DOM keydown events (when focus is inside the dropdown, e.g. search input)
function handleDOMKeyDown(event: KeyboardEvent): void {
  if (handleKeyDown(event)) {
    event.preventDefault()
  }
}

async function selectDocumentType(type: DocumentTypeValue) {
  selectedDocumentType.value = type
  loading.value = true
  currentStep.value = Steps.DOCUMENT_LIST
  selectedIndex.value = 0
  await fetchDocuments()

  await nextTick()
  searchFocusedAt.value = Date.now()
}

async function fetchPermanentDocumentsData(
  baseParams: Record<string, ParamsValue>
) {
  const response = await fetchPermanentDocuments({
    ...baseParams,
    with_documents: true,
    page: page.value,
  })

  if (!response) return

  fetchedEntries.value = (response.permanentEntries || []).map(
    (entry: DocumentEntry) => ({
      ...entry,
      link: entry.documentUrl || '',
    })
  )
  permanentCategory.value = response.permanentCategory || null
  page.value = response.currentPage || 1
  totalPages.value = response.totalPages || 1
}

async function fetchPeriodSpecificDocumentsData(
  baseParams: Record<string, ParamsValue>
) {
  const response = await fetchPeriodSpecificDocuments(baseParams)

  if (!response) return

  fetchedEntries.value = (response || []).map((entry: DocumentEntry) => ({
    ...entry,
    link: window.Silverfin.Routes.document_path({
      firm_id: window.Silverfin.currentFirmId,
      company_id: getCompanyId(),
      id: entry.id,
      current_ledger_id: window.Silverfin.currentLedgerId(),
      _options: true,
    }),
  }))
}

function resetFetchedState() {
  fetchedEntries.value = []
  permanentCategory.value = null
  page.value = 1
  totalPages.value = 1
}

async function fetchDocuments(params: Record<string, ParamsValue> = {}) {
  if (isCached(selectedDocumentType.value)) {
    loading.value = false
    return
  }

  loading.value = true
  const baseParams = buildBaseParams(params)

  try {
    if (selectedDocumentType.value === DocumentTypeValues.PERMANENT_DOCUMENT) {
      await fetchPermanentDocumentsData(baseParams)
    } else if (
      selectedDocumentType.value === DocumentTypeValues.PERIOD_SPECIFIC_DOCUMENT
    ) {
      await fetchPeriodSpecificDocumentsData(baseParams)
    }
  } catch (error) {
    SfLog.error('Error fetching documents', { extra: { error } })
    resetFetchedState()
  } finally {
    loading.value = false
  }
}

function navigateToCategory(category: PermanentCategory) {
  page.value = 1
  fetchDocuments({ permanent_category_id: category.id })
}

function handleDocumentSelection(entry: DocumentEntry) {
  if (entry.category) {
    navigateToCategory(entry.category)
    return
  }

  if (selectionMade.value) return
  selectionMade.value = true

  props.command({
    id: entry.title,
    link: entry.link,
    mentionType: selectedDocumentType.value as DocumentTypeValue,
  })
}

function viewDocument(entry: DocumentEntry) {
  if (entry.link) {
    window.open(entry.link, '_blank', 'noopener,noreferrer')
  }
}

function changePage(newPage: number) {
  page.value = newPage
  fetchDocuments(
    permanentCategory.value
      ? { permanent_category_id: permanentCategory.value.id }
      : {}
  )
}

function goBackToTypeSelection() {
  currentStep.value = Steps.TYPE_SELECTION
  selectedDocumentType.value = null
  searchQuery.value = ''
  fetchedEntries.value = []
  permanentCategory.value = null
  page.value = 1
  totalPages.value = 1
  selectedIndex.value = 0
}

function updateSearchQuery(value: string | null) {
  searchQuery.value = value || ''
}

function clearSearch() {
  searchQuery.value = ''
}
</script>
